import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { DEFAULT_SITE } from './siteDefaults';
import { PRODUCTS } from './products';
import { sanitizeProductDescription } from './productHtml';
import { apiUrl } from './apiBase';

const HEADER_LOGO_URL =
  'https://res.cloudinary.com/dnw8qhp3h/image/upload/v1775968208/WhatsApp_Image_2026-04-12_at_10.13.22_AM_vzenep.jpg';

function ArrowUpRight({ className }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" aria-hidden>
      <path
        d="M4 12L12 4M12 4H6M12 4V10"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function productItemsFromSite(site) {
  const p = site?.products;
  if (p && typeof p === 'object' && Array.isArray(p.items)) return p.items;
  return PRODUCTS;
}

export default function ProductDetail() {
  const { productId } = useParams();
  const [site, setSite] = useState(() => JSON.parse(JSON.stringify(DEFAULT_SITE)));
  const [ready, setReady] = useState(false);

  useEffect(() => {
    fetch(apiUrl('/api/site'))
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setSite)
      .catch(() => {})
      .finally(() => setReady(true));
  }, []);

  const items = useMemo(() => productItemsFromSite(site), [site]);

  const decodedId = productId ? decodeURIComponent(productId) : '';
  const product = items.find((p) => p.id === decodedId);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [productId]);

  useEffect(() => {
    if (!ready) return;
    const base = 'Rafid Seed';
    if (!product) {
      document.title = `Product not found | ${base}`;
      return () => {
        document.title = base;
      };
    }
    document.title = `${product.title} | ${base}`;
    return () => {
      document.title = base;
    };
  }, [ready, product]);

  if (!ready) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center bg-white text-neutral-500">Loading…</div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-white text-neutral-900">
        <header className="border-b border-neutral-200 bg-white">
          <div className="mx-auto flex max-w-shell items-center justify-between gap-4 px-4 py-4 sm:px-6 lg:px-8">
            <Link to="/" className="inline-flex items-center gap-2.5 no-underline">
              <img
                src={HEADER_LOGO_URL}
                alt=""
                className="h-10 w-10 shrink-0 rounded-full object-cover ring-1 ring-neutral-200"
              />
              <span className="text-sm font-bold text-brand-forest">Rafid Seed</span>
            </Link>
          </div>
        </header>
        <div className="mx-auto max-w-2xl px-4 py-20 text-center sm:px-6">
          <h1 className="text-2xl font-bold text-neutral-900">Product not found</h1>
          <p className="mt-3 text-neutral-600">This item may have been removed or the link is incorrect.</p>
          <Link
            to="/#products"
            className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-brand-lime no-underline hover:text-brand-lime-hover"
          >
            ← Back to products
          </Link>
        </div>
      </div>
    );
  }

  const { title, image, alt, description, id } = product;
  const safeDesc = sanitizeProductDescription(description);

  return (
    <div className="min-h-screen bg-brand-cream/40 text-neutral-900">
      <header className="sticky top-0 z-50 border-b border-neutral-200/90 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/90">
        <div className="mx-auto flex max-w-shell items-center justify-between gap-4 px-4 py-3 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center gap-2.5 no-underline">
            <img
              src={HEADER_LOGO_URL}
              alt=""
              className="h-10 w-10 shrink-0 rounded-full object-cover ring-1 ring-neutral-200 sm:h-11 sm:w-11"
            />
            <span className="text-sm font-bold text-brand-forest sm:text-base">Rafid Seed</span>
          </Link>
          <Link
            to="/#products"
            className="text-sm font-semibold text-brand-lime no-underline transition-colors hover:text-brand-lime-hover"
          >
            All products
          </Link>
        </div>
      </header>

      <nav className="border-b border-neutral-200/80 bg-white/80 px-4 py-3 text-sm text-neutral-600 sm:px-6 lg:px-8">
        <div className="mx-auto flex max-w-shell flex-wrap items-center gap-x-2 gap-y-1">
          <Link to="/" className="font-medium text-brand-olive no-underline hover:underline">
            Home
          </Link>
          <span aria-hidden className="text-neutral-400">
            /
          </span>
          <Link to="/#products" className="font-medium text-brand-olive no-underline hover:underline">
            Products
          </Link>
          <span aria-hidden className="text-neutral-400">
            /
          </span>
          <span className="truncate text-neutral-500">{title}</span>
        </div>
      </nav>

      <main className="mx-auto max-w-shell px-4 py-10 sm:px-6 sm:py-12 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-2 lg:gap-12 lg:items-start">
          <div className="overflow-hidden rounded-2xl border border-neutral-200/90 bg-white shadow-sm ring-1 ring-black/5 lg:sticky lg:top-24">
            <div className="aspect-square bg-neutral-100 sm:aspect-[4/3] lg:aspect-square">
              <img src={image} alt={alt || title} className="h-full w-full object-cover" />
            </div>
          </div>

          <div>
            <h1 className="text-3xl font-bold leading-tight tracking-tight text-neutral-900 sm:text-4xl">
              {title}
            </h1>
            <p className="mt-3 text-xs font-medium uppercase tracking-wide text-neutral-400">Ref. {id}</p>

            {safeDesc ? (
              <div
                className="prose-product mt-8 border-t border-neutral-200 pt-8 text-base leading-relaxed text-neutral-700 [&_a]:font-semibold [&_a]:text-brand-olive [&_a]:underline [&_li]:my-1 [&_ol]:my-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_p:last-child]:mb-0 [&_p]:mb-4 [&_ul]:my-3 [&_ul]:list-disc [&_ul]:pl-5"
                dangerouslySetInnerHTML={{ __html: safeDesc }}
              />
            ) : (
              <p className="mt-8 border-t border-neutral-200 pt-8 text-neutral-600">
                Full description coming soon. Contact us for variety details, availability, and agronomic fit.
              </p>
            )}

            <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <Link
                to="/#contact"
                className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-2xl bg-brand-lime px-6 py-3 text-sm font-semibold text-brand-forest no-underline transition-colors hover:bg-brand-lime-hover"
              >
                Request quote / contact
                <ArrowUpRight className="h-4 w-4 shrink-0" />
              </Link>
              <Link
                to="/#products"
                className="inline-flex min-h-[48px] items-center justify-center rounded-2xl border border-neutral-300 bg-white px-6 py-3 text-sm font-semibold text-neutral-800 no-underline transition-colors hover:bg-neutral-50"
              >
                ← Back to catalog
              </Link>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
